<template>
  <div>
    <b-alert
      v-if="loadError"
      variant="danger"
      show
      class="mb-1"
    >
      <div class="alert-body">
        {{ loadError }}
      </div>
    </b-alert>

    <!-- สรุปยอดคิดจากทั้งชุด ไม่ใช่เฉพาะหน้าที่เปิดอยู่ ตัวเลขจึงไม่เปลี่ยนตามหน้าที่กด -->
    <b-row class="mb-1">
      <b-col
        v-for="stat in stats"
        :key="stat.key"
        md="3"
        sm="6"
      >
        <b-card
          class="mb-1 stat-card"
          :class="{ 'stat-card-active': isStatActive(stat) }"
          body-class="py-1"
          @click="applyStat(stat)"
        >
          <h3 class="mb-0">
            {{ Number(stat.value || 0).toLocaleString('en-US') }}
          </h3>
          <small class="text-muted">{{ stat.label }}</small>
        </b-card>
      </b-col>
    </b-row>

    <b-card :title="cardTitle">
      <b-row class="mb-1">
        <b-col md="4">
          <b-form-group
            label="ค้นหา (ชื่อ / รหัส / id)"
            label-for="search"
          >
            <b-form-input
              id="search"
              v-model="search"
              placeholder="เช่น Airport หรือ 43"
              :disabled="isLoading"
              @keyup.enter="onFilterChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="โซน"
            label-for="zone-state"
          >
            <b-form-select
              id="zone-state"
              v-model="zoneFilter"
              :options="zoneOptions"
              :disabled="isLoading"
              @change="onZoneChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="สาเหตุ"
            label-for="reason"
          >
            <!-- ดูเฉพาะที่มีโซนแล้ว = ทุกแถวสาเหตุเดียวกันหมด เลือกต่อไม่มีความหมาย -->
            <b-form-select
              id="reason"
              v-model="reasonFilter"
              :options="reasonOptions"
              :disabled="isLoading || zoneFilter === 'with'"
              @change="onFilterChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            label="เปิด/ปิด"
            label-for="active"
          >
            <b-form-select
              id="active"
              v-model="activeFilter"
              :options="activeOptions"
              :disabled="isLoading"
              @change="onFilterChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="สถานะงานค้าง"
            label-for="status"
          >
            <b-form-select
              id="status"
              v-model="statusFilter"
              :options="statusOptions"
              :disabled="isLoading"
              @change="onFilterChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="ที่อยู่"
            label-for="has-address"
          >
            <b-form-select
              id="has-address"
              v-model="hasAddressFilter"
              :options="hasAddressOptions"
              :disabled="isLoading"
              @change="onFilterChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="สถานะตรวจพิกัด"
            label-for="coord-status"
          >
            <b-form-select
              id="coord-status"
              v-model="coordStatusFilter"
              :options="coordStatusOptions"
              :disabled="isLoading"
              @change="onFilterChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="จังหวัด"
            label-for="province"
          >
            <b-form-select
              id="province"
              v-model="provinceFilter"
              :options="provinceOptions"
              :disabled="isLoading"
              @change="onFilterChange"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="3"
          class="d-flex align-items-end mb-1"
        >
          <b-button
            variant="primary"
            class="mr-1"
            :disabled="isLoading"
            @click="onFilterChange"
          >
            ค้นหา
          </b-button>
          <b-button
            variant="outline-secondary"
            :disabled="isLoading || isExporting"
            @click="exportCsv"
          >
            <b-spinner
              v-if="isExporting"
              small
              class="mr-50"
            />
            CSV
          </b-button>
        </b-col>
      </b-row>

      <b-table
        striped
        hover
        responsive
        :items="places"
        :fields="fields"
        :busy="isLoading"
        show-empty
        empty-text="ไม่พบ place ที่ตรงเงื่อนไข"
      >
        <template #cell(place_name)="data">
          <span :title="data.item.place_name">{{ data.item.place_name || '—' }}</span>
        </template>
        <template #cell(reason)="data">
          <b-badge :variant="reasonVariant(data.item.reason)">
            {{ reasonLabel(data.item.reason) }}
          </b-badge>
        </template>
        <!--
          โซนที่ผูกอยู่ — ว่างเสมอในขอบเขต "ยังไม่มีโซน" มีค่าเมื่อดูฝั่งที่ผูกแล้ว

          แยกสองทางตามที่ผูกมาจริง: place_zone คือทางที่ระบบค้นหาใช้ ส่วน place.zone_id
          เป็นคอลัมน์เก่าที่มักค้างไว้ผิด โชว์แยกไว้ให้เห็นว่าอันไหนคือของจริง
        -->
        <template #cell(zone_names)="data">
          <span
            v-if="data.item.zone_names"
            :title="data.item.zone_names"
          >{{ data.item.zone_names }}</span>
          <span
            v-else
            class="text-muted"
          >—</span>
          <small
            v-if="data.item.zone_col_name && data.item.zone_col_name !== data.item.zone_names"
            class="d-block text-muted"
          >
            คอลัมน์เก่า:
            <b-badge variant="light-warning">{{ data.item.zone_col_name }}</b-badge>
          </small>
        </template>
        <!--
          เช็คพิกัดแบบเดียวกับหน้า coord-review — พิกัดปัจจุบัน, พิกัดที่แนะนำ และระยะห่าง
          วางไว้ติดกันเพื่อให้เปิดแผนที่เทียบด้วยตาได้ทั้งคู่
        -->
        <template #cell(gps)="data">
          <template v-if="Number(data.item.has_latlng)">
            <b-link
              :href="mapUrl(data.item.lat, data.item.lng)"
              target="_blank"
              rel="noopener"
            >{{ fmt(data.item.lat) }}, {{ fmt(data.item.lng) }}</b-link>
          </template>
          <b-badge
            v-else
            variant="light-danger"
          >
            ไม่มีพิกัด
          </b-badge>

          <small
            v-if="data.item.suggested_lat"
            class="d-block text-muted"
          >
            แนะนำ
            <b-link
              :href="mapUrl(data.item.suggested_lat, data.item.suggested_lng)"
              target="_blank"
              rel="noopener"
            >{{ fmt(data.item.suggested_lat) }}, {{ fmt(data.item.suggested_lng) }}</b-link>
            <b-badge
              :variant="severityVariant(data.item.severity)"
              class="ml-25"
            >
              ต่าง {{ distanceText(data.item.suggested_distance_m) }}
            </b-badge>
          </small>
          <small
            v-else
            class="d-block text-muted"
          >ไม่มีพิกัดแนะนำให้เทียบ</small>

          <!--
            ตัดสินใจกับ "พิกัดที่แนะนำ" ได้ตรงนี้เลย — วางติดกับตัวเลขที่ใช้ตัดสิน
            ไม่ใช่ไปรวมกับปุ่มอื่นในคอลัมน์ actions ที่ไม่เกี่ยวกัน
            ทั้งสองปุ่มยิง endpoint เดียวกับหน้า coord-review
          -->
          <div
            v-if="data.item.suggested_lat && data.item.coord_review_status !== 'applied'"
            class="d-flex flex-wrap mt-25"
          >
            <b-button
              size="sm"
              variant="outline-success"
              class="mr-25 mb-25 py-25 px-50"
              :disabled="reviewingId === data.item.place_id"
              @click="applySuggested(data.item)"
            >
              <b-spinner
                v-if="reviewingId === data.item.place_id"
                small
                class="mr-25"
              />
              ใช้ค่าใหม่
            </b-button>
            <b-button
              size="sm"
              variant="outline-secondary"
              class="mb-25 py-25 px-50"
              :disabled="reviewingId === data.item.place_id"
              @click="markReviewed(data.item, 'rejected')"
            >
              เดิมถูกแล้ว
            </b-button>
          </div>

          <!-- สถานะตรวจพิกัดจากหน้า coord-review — บอกว่ารายการนี้มีคนแตะไปแล้วหรือยัง -->
          <b-badge
            :variant="coordStatusVariant(data.item.coord_review_status)"
            class="mt-25"
          >
            {{ coordStatusLabel(data.item.coord_review_status) }}
          </b-badge>
          <small
            v-if="data.item.coord_applied_at"
            class="d-block text-muted"
          >แก้เมื่อ {{ shortDate(data.item.coord_applied_at) }}</small>
        </template>
        <!-- สถานะ = ขั้นถัดไปที่ต้องทำ คิดสดจาก DB ทุกครั้ง กดปุ่มแล้วจะขยับเอง -->
        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)">
            {{ statusLabel(data.item.status) }}
          </b-badge>
          <small
            v-if="data.item.province_th"
            class="d-block text-muted"
          >
            {{ data.item.province_th }}
            <span v-if="data.item.amphure_th">/ {{ data.item.amphure_th }}</span>
            <span v-if="data.item.tambon_th">/ {{ data.item.tambon_th }}</span>
          </small>
        </template>
        <template #cell(is_active)="data">
          <b-badge :variant="Number(data.item.is_active) ? 'light-success' : 'light-secondary'">
            {{ Number(data.item.is_active) ? 'เปิด' : 'ปิด' }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <b-button
            size="sm"
            variant="outline-primary"
            class="mr-50"
            :to="{ name: 'pin-edit', params: { id: data.item.place_id } }"
          >
            แก้ไข
          </b-button>
          <b-button
            size="sm"
            variant="outline-success"
            class="mr-50"
            :disabled="previewingId === data.item.place_id"
            @click="openAreaModal(data.item)"
          >
            <b-spinner
              v-if="previewingId === data.item.place_id"
              small
              class="mr-50"
            />
            ผูกที่อยู่
          </b-button>
          <b-button
            size="sm"
            variant="outline-warning"
            class="mr-50"
            @click="openCoordModal(data.item)"
          >
            แก้พิกัด
          </b-button>
          <b-button
            size="sm"
            :variant="data.item.derived_recalc_at ? 'flat-success' : 'outline-info'"
            :disabled="!Number(data.item.has_latlng) || recalcingId === data.item.place_id"
            @click="openRecalcModal(data.item)"
          >
            <b-spinner
              v-if="recalcingId === data.item.place_id"
              small
              class="mr-50"
            />
            {{ data.item.derived_recalc_at ? 'คำนวณแล้ว (ทำซ้ำ)' : 'คำนวณโซน/สถานี' }}
          </b-button>
        </template>
      </b-table>

      <div class="d-flex justify-content-between align-items-center flex-wrap">
        <small class="text-muted">
          พบ {{ Number(totalRows).toLocaleString('en-US') }} รายการ
          <span v-if="totalRows">· หน้า {{ currentPage }} จาก {{ totalPages }}</span>
        </small>
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="right"
          class="mb-0"
          @change="onPageChange"
        />
      </div>
    </b-card>

    <b-alert
      variant="secondary"
      show
    >
      <div class="alert-body">
        <small>
          <strong>นับยังไง:</strong> place ผูกโซนได้ 2 ทาง — ตาราง <code>place_zone</code> (ทางหลักที่ระบบค้นหาใช้)
          และคอลัมน์เก่า <code>place.zone_id</code> รายงานนี้นับว่า “ไม่มีโซน” ต่อเมื่อ<strong>ไม่มีทั้งสองทาง</strong> ·
          <strong>ผูกไว้แต่โซนถูกลบ</strong> คือเคยผูกไว้แล้วแต่โซนนั้นถูกลบไป ซึ่งมักชี้กลับไปโซนเดียวกัน
          แก้ทีเดียวได้หลายรายการ
        </small>
        <!--
          ⚠️ เครดิตนี้เป็นเงื่อนไขของสัญญาอนุญาต CC BY-IGO ของชุดข้อมูลขอบเขตการปกครอง
          ที่ปุ่ม “ผูกที่อยู่” ใช้ — ห้ามถอดออก
        -->
        <small class="d-block text-muted mt-50">
          ขอบเขต จังหวัด/อำเภอ/ตำบล: กรมแผนที่ทหาร (Royal Thai Survey Department)
          เผยแพร่ผ่าน <a
            href="https://data.humdata.org/dataset/cod-ab-tha"
            target="_blank"
            rel="noopener"
          >OCHA HDX</a> · สัญญาอนุญาต CC BY-IGO 3.0
        </small>
      </div>
    </b-alert>

    <!--
      ยืนยันก่อนเขียนเสมอ — ข้อเสนอระดับตำบลที่มาจาก place ข้างเคียงผิดได้ราว 1 ใน 5
      (ตัวเลขที่วัดไว้อยู่ใน api/helpers/place-admin-area.helper.js) จึงกางให้ดูทั้ง
      ของเดิม ของใหม่ และที่มาของคำตอบ ก่อนให้กดยืนยัน
    -->
    <b-modal
      id="area-modal"
      v-model="isAreaModalOpen"
      title="ยืนยันการผูกที่อยู่"
      ok-title="ยืนยัน บันทึกลงฐานข้อมูล"
      cancel-title="ยกเลิก"
      ok-variant="success"
      cancel-variant="outline-secondary"
      :ok-disabled="!canApplyArea || isApplyingArea"
      :busy="isApplyingArea"
      @ok.prevent="applyArea"
    >
      <div v-if="areaTarget">
        <h5 class="mb-1">
          {{ areaTarget.place_name || '—' }}
          <small class="text-muted">id {{ areaTarget.place_id }}</small>
        </h5>

        <b-alert
          v-if="areaError"
          variant="danger"
          show
        >
          <div class="alert-body">
            {{ areaError }}
          </div>
        </b-alert>

        <div
          v-else-if="isPreviewingArea"
          class="text-center py-2"
        >
          <b-spinner
            small
            class="mr-50"
          />
          กำลังหาข้อมูลที่อยู่...
        </div>

        <template v-else-if="areaSuggestion">
          <b-badge
            :variant="confidenceVariant"
            class="mb-1"
          >
            {{ confidenceLabel }}
          </b-badge>

          <table class="table table-sm mb-1">
            <thead>
              <tr>
                <th />
                <th>ของเดิม</th>
                <th>จะเปลี่ยนเป็น</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in areaRows"
                :key="row.label"
              >
                <td class="text-muted">
                  {{ row.label }}
                </td>
                <td>{{ row.before }}</td>
                <td :class="row.changed ? 'font-weight-bold text-success' : ''">
                  {{ row.after }}
                </td>
              </tr>
            </tbody>
          </table>

          <small class="text-muted d-block">{{ sourceNote }}</small>
        </template>

        <b-alert
          v-else
          variant="warning"
          show
        >
          <div class="alert-body">
            <small>
              หาที่อยู่ให้ place นี้ไม่ได้ — ไม่มีที่อยู่จาก Google เก็บไว้
              และไม่มี place ที่ผูกตำบลแล้วอยู่ในรัศมี 2 กม. ต้องกรอกเองที่หน้าแก้ไข
            </small>
          </div>
        </b-alert>
      </div>
    </b-modal>

    <!--
      คำนวณโซน/สถานี — dry run ก่อน แล้วค่อยยืนยัน เหมือนหน้า coord-review เป๊ะ
      เพราะการคำนวณนี้ลบ place_zone / place_transits ของเดิมทิ้งทั้งชุดก่อนเขียนใหม่
    -->
    <b-modal
      id="recalc-modal"
      v-model="isRecalcModalOpen"
      title="ยืนยันการคำนวณโซน / สถานี"
      ok-title="ยืนยัน บันทึกลงฐานข้อมูล"
      cancel-title="ยกเลิก"
      ok-variant="success"
      cancel-variant="outline-secondary"
      :ok-disabled="!recalcPreview || isSavingRecalc"
      :busy="isSavingRecalc"
      @ok.prevent="confirmRecalc"
    >
      <div v-if="recalcTarget">
        <h5 class="mb-1">
          {{ recalcTarget.place_name || '—' }}
          <small class="text-muted">id {{ recalcTarget.place_id }}</small>
        </h5>

        <b-alert
          v-if="recalcError"
          variant="danger"
          show
        >
          <div class="alert-body">
            {{ recalcError }}
          </div>
        </b-alert>

        <div
          v-else-if="isPreviewingRecalc"
          class="text-center py-2"
        >
          <b-spinner
            small
            class="mr-50"
          />
          กำลังคำนวณผลก่อนบันทึก...
        </div>

        <template v-else-if="recalcPreview">
          <h6 class="text-danger">
            โซน
          </h6>

          <!-- ผลจากการคำนวณด้วย polygon — อ่านอย่างเดียว ไว้เทียบกับที่เลือกเอง -->
          <p class="mb-25">
            <small class="text-muted">ผลคำนวณจากพิกัด:</small>
            <span v-if="computedZoneIds.length">
              {{ recalcPreview.zones.map(z => z.name).join(', ') }}
            </span>
            <span
              v-else
              class="text-muted"
            >{{ recalcPreview.zone_reason || 'ไม่พบโซนที่ครอบพิกัดนี้' }}</span>
          </p>

          <!--
            เลือกโซนเองได้ — polygon ครอบแค่ กทม. (114 จาก 117 โซน) place ต่างจังหวัด
            จึงไม่ตกโซนไหนเลยเป็นปกติ และบางเคสคนก็รู้ดีกว่าเส้นขอบเขต
          -->
          <b-form-group
            label="โซนที่จะบันทึก"
            label-for="recalc-zones"
            class="mb-25"
          >
            <v-select
              id="recalc-zones"
              v-model="recalcZoneIds"
              multiple
              label="title"
              :reduce="o => o.id"
              :options="zoneChoices"
              :loading="isLoadingZones"
              :disabled="!!zoneLoadError"
              placeholder="เลือกโซน"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            />
          </b-form-group>

          <div class="mb-1">
            <b-button
              size="sm"
              variant="outline-secondary"
              class="mr-50 py-25 px-50"
              :disabled="!zonesChanged"
              @click="resetZones"
            >
              ย้อนเป็นค่าตั้งต้น
            </b-button>
            <small
              v-if="zonesChanged"
              class="text-primary"
            >แก้เองแล้ว — จะบันทึกตามที่เลือก ไม่ใช่ผลคำนวณ</small>
          </div>

          <b-alert
            v-if="zoneLoadError"
            variant="warning"
            show
          >
            <div class="alert-body">
              <small>{{ zoneLoadError }} — เลือกโซนเองไม่ได้ กดยืนยันจะใช้ผลคำนวณตามปกติ</small>
            </div>
          </b-alert>

          <!--
            เตือนเฉพาะตอนที่กดแล้วจะลบของที่มีอยู่จริง — place ส่วนใหญ่บนหน้านี้
            ไม่เคยมีโซนอยู่แล้ว (2,383 จาก 2,430) ถ้าเตือนทุกครั้งที่ช่องว่างจะกลายเป็นเสียงรบกวน
          -->
          <b-alert
            v-else-if="!recalcZoneIds.length && recalcPreview.zone_current && recalcPreview.zone_current.length"
            variant="warning"
            show
          >
            <div class="alert-body">
              <small>
                ยืนยันแล้วจะ<strong>ลบโซนที่ผูกไว้ทั้งหมด</strong>
                ({{ recalcPreview.zone_current.map(z => z.name).join(', ') }})
              </small>
            </div>
          </b-alert>

          <h6 class="text-danger">
            สถานี
            <small
              v-if="recalcPreview.transit_radius_m"
              class="text-muted"
            >(ในรัศมี {{ (recalcPreview.transit_radius_m / 1000).toFixed(0) }} กม.)</small>
          </h6>
          <ul
            v-if="recalcPreview.transits && recalcPreview.transits.length"
            class="pl-2 mb-1"
          >
            <li
              v-for="t in recalcPreview.transits"
              :key="'t' + t.types + t.id"
            >
              <strong>{{ t.types }}</strong> {{ t.name }} — {{ t.distance }} ม.
            </li>
          </ul>
          <p
            v-else
            class="text-muted mb-1"
          >
            ไม่พบสถานีใกล้เคียง
          </p>

          <b-alert
            variant="secondary"
            show
          >
            <div class="alert-body">
              <small>
                ยืนยันแล้วจะ<strong>ลบโซนและสถานีที่ผูกไว้เดิมทิ้งทั้งชุด</strong>แล้วเขียนใหม่ตามรายการข้างบน
                <span v-if="zonesChanged">(โซนใช้ตามที่เลือกเอง ส่วนสถานียังคำนวณจากพิกัด)</span>
              </small>
            </div>
          </b-alert>
        </template>
      </div>
    </b-modal>

    <!--
      แก้พิกัดเอง — ยิงไปที่ POST /place/v1/coord-review/set-coord ตัวเดียวกับที่หน้า
      coord-review ใช้ ไม่ได้เขียน endpoint ใหม่ จึงได้ validation เดิม (ห้ามเป็น 0,
      ต้องอยู่ในช่วง lat -90..90 / lng -180..180) และเก็บ prev_lat/prev_lng ให้ย้อนได้เหมือนกัน
    -->
    <b-modal
      id="coord-modal"
      v-model="isCoordModalOpen"
      title="แก้ไขพิกัด (lat / lng)"
      ok-title="บันทึกพิกัด"
      cancel-title="ยกเลิก"
      ok-variant="warning"
      cancel-variant="outline-secondary"
      :ok-disabled="!canSaveCoord || isSavingCoord"
      :busy="isSavingCoord"
      @ok.prevent="saveCoord"
    >
      <div v-if="coordTarget">
        <h5 class="mb-1">
          {{ coordTarget.place_name || '—' }}
          <small class="text-muted">id {{ coordTarget.place_id }}</small>
        </h5>

        <b-alert
          v-if="coordError"
          variant="danger"
          show
        >
          <div class="alert-body">
            {{ coordError }}
          </div>
        </b-alert>

        <p class="mb-1">
          <small class="text-muted">พิกัดปัจจุบัน</small><br>
          <b-link
            v-if="Number(coordTarget.has_latlng)"
            :href="mapUrl(coordTarget.lat, coordTarget.lng)"
            target="_blank"
            rel="noopener"
          >{{ fmt(coordTarget.lat) }}, {{ fmt(coordTarget.lng) }}</b-link>
          <span
            v-else
            class="text-muted"
          >ไม่มีพิกัด</span>
        </p>

        <p
          v-if="coordTarget.suggested_lat"
          class="mb-1"
        >
          <small class="text-muted">
            พิกัดที่แนะนำ<span v-if="coordTarget.suggested_source"> (จาก {{ coordTarget.suggested_source }})</span>
          </small><br>
          <b-link
            :href="mapUrl(coordTarget.suggested_lat, coordTarget.suggested_lng)"
            target="_blank"
            rel="noopener"
          >{{ fmt(coordTarget.suggested_lat) }}, {{ fmt(coordTarget.suggested_lng) }}</b-link>
          <b-badge
            :variant="severityVariant(coordTarget.severity)"
            class="ml-25"
          >
            ต่าง {{ distanceText(coordTarget.suggested_distance_m) }}
          </b-badge>
          <b-button
            size="sm"
            variant="outline-primary"
            class="ml-50"
            @click="useSuggestedCoord"
          >
            ใช้ค่านี้
          </b-button>
        </p>

        <b-row>
          <b-col md="6">
            <b-form-group
              label="Latitude"
              label-for="coord-lat"
            >
              <b-form-input
                id="coord-lat"
                v-model="coordLat"
                placeholder="เช่น 13.78796"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group
              label="Longitude"
              label-for="coord-lng"
            >
              <b-form-input
                id="coord-lng"
                v-model="coordLng"
                placeholder="เช่น 100.55295"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-alert
          :variant="coordProblem ? 'danger' : 'secondary'"
          show
        >
          <div class="alert-body">
            <small>
              <template v-if="coordProblem">{{ coordProblem }}</template>
              <template v-else>
                ค่าเดิมจะถูกเก็บไว้ที่ <code>prev_lat</code> / <code>prev_lng</code> ย้อนกลับได้ที่หน้า coord-review ·
                ⚠️ โซนและสถานีที่ผูกไว้ <strong>จะยังเป็นของพิกัดเก่า</strong> จนกว่าจะสั่งคำนวณใหม่ที่หน้านั้น
              </template>
            </small>
          </div>
        </b-alert>
      </div>
    </b-modal>
  </div>
</template>

<script>
/*
 * Place ที่ยังไม่ถูกผูกโซน — ไล่เก็บงานค้าง
 *
 * อ่านจาก POST /place/v1/without-zone (ดู model/place-zone-audit.js ฝั่ง API ว่านับยังไง)
 */
import {
  BCard, BTable, BBadge, BFormSelect, BFormGroup, BFormInput,
  BButton, BRow, BCol, BAlert, BPagination, BSpinner, BModal, BLink,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from '@axios'

/* ดึงทีละก้อนตอน export ไม่งั้นสองพันกว่าแถวในครั้งเดียวจะช้าและเสี่ยง timeout */
const EXPORT_CHUNK = 1000

/*
 * สถานะงานค้าง — ป้ายและสีอยู่ที่เดียว ให้ตรงกับนิยามฝั่ง API (PROGRESS ใน
 * api/model/place-zone-audit.js) เพิ่มสถานะที่นั่นแล้วต้องเพิ่มที่นี่ด้วย
 */
const PROGRESS = {
  no_coord: { label: 'ยังไม่มีพิกัด', variant: 'light-danger' },
  no_address: { label: 'ยังไม่ผูกที่อยู่', variant: 'light-warning' },
  address_partial: { label: 'มีอำเภอ ยังไม่มีตำบล', variant: 'light-info' },
  need_recalc: { label: 'รอคำนวณโซน/สถานี', variant: 'light-primary' },
  ready: { label: 'เหลือแค่ผูกโซน', variant: 'light-success' },
}

/*
 * สถานะตรวจพิกัด — ป้ายและสีชุดเดียวกับหน้า place/coord-review เป๊ะ ๆ
 * ค่ามาจากคอลัมน์ place.coord_review_status ตัวเดียวกัน สองหน้าจึงพูดภาษาเดียวกัน
 */
const COORD_STATUS = {
  pending: { label: 'รอตรวจ', variant: 'light-warning' },
  approved: { label: 'ใช้ค่าใหม่', variant: 'light-success' },
  rejected: { label: 'เดิมถูกแล้ว', variant: 'light-secondary' },
  no_source: { label: 'ไม่มีข้อมูลเทียบ', variant: 'light-danger' },
  applied: { label: 'ใช้ค่าใหม่แล้ว', variant: 'light-primary' },
}

/*
 * สาเหตุที่ยังไม่มีโซน — ป้ายและสีอยู่ที่เดียว ตรงกับ REASON ใน
 * api/model/place-zone-audit.js
 *
 * has_zone โผล่เฉพาะตอนเลือกขอบเขต "มีโซนแล้ว" / "ทั้งหมด" จึงไม่มีในเมนูกรอง
 * (ตัวกรองโซนทำหน้าที่นั้นอยู่แล้ว) แต่ต้องมีป้ายไว้ให้ badge ในตารางแสดงถูก
 */
const REASON = {
  no_link: { label: 'ไม่เคยผูกโซน', variant: 'light-secondary', selectable: true },
  zone_deleted: { label: 'ผูกไว้แต่โซนถูกลบ', variant: 'light-warning', selectable: true },
  has_zone: { label: 'มีโซนแล้ว', variant: 'light-success', selectable: false },
}

/* ขอบเขตของรายการ — ค่าเดียวกับ ZONE_SCOPES ใน api/model/place-zone-audit.js */
const ZONE_STATES = {
  without: { label: 'ยังไม่มีโซน', count: 'without_zone', title: 'Place ที่ยังไม่มีโซน' },
  with: { label: 'มีโซนแล้ว', count: 'with_zone', title: 'Place ที่ผูกโซนแล้ว' },
  all: { label: 'ทั้งหมด', count: 'total_places', title: 'Place ทั้งหมด' },
}

/* สีของระดับความต่างระหว่างพิกัดเดิมกับที่แนะนำ — ชุดเดียวกับหน้า coord-review */
const SEVERITY_VARIANT = {
  critical: 'light-danger',
  high: 'light-warning',
  medium: 'light-info',
  low: 'light-secondary',
  same: 'light-success',
  no_coord: 'light-danger',
  unknown: 'light-secondary',
}

export default {
  components: {
    BCard,
    BTable,
    BBadge,
    BFormSelect,
    BFormGroup,
    BFormInput,
    BButton,
    BRow,
    BCol,
    BAlert,
    BPagination,
    BSpinner,
    BModal,
    BLink,
    vSelect,
  },
  data() {
    return {
      places: [],
      summary: {},
      fields: [
        { key: 'place_id', label: 'ID' },
        { key: 'place_name', label: 'ชื่อ Place' },
        { key: 'code', label: 'รหัส' },
        { key: 'reason', label: 'สาเหตุ' },
        { key: 'zone_names', label: 'โซน' },
        { key: 'gps', label: 'พิกัด (GPS)' },
        { key: 'status', label: 'สถานะ' },
        { key: 'is_active', label: 'สถานะ', class: 'text-center' },
        { key: 'actions', label: '' },
      ],
      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      isLoading: false,
      isExporting: false,
      loadError: '',
      search: '',
      reasonFilter: '',
      activeFilter: '',
      zoneFilter: 'without',
      reasonOptions: [
        { value: '', text: 'ทั้งหมด' },
        ...Object.entries(REASON)
          .filter(([, v]) => v.selectable)
          .map(([value, v]) => ({ value, text: v.label })),
      ],
      activeOptions: [
        { value: '', text: 'ทั้งหมด' },
        { value: '1', text: 'เปิดใช้งาน' },
        { value: '0', text: 'ปิดใช้งาน' },
      ],

      /* ── ผูก จังหวัด/อำเภอ/ตำบล/รหัสไปรษณีย์ ── */
      isAreaModalOpen: false,
      isPreviewingArea: false,
      isApplyingArea: false,
      previewingId: null,
      areaTarget: null,
      areaCurrent: null,
      areaSuggestion: null,
      areaError: '',

      statusFilter: '',
      coordStatusFilter: '',
      hasAddressFilter: '',
      provinceFilter: '',
      provinces: [],
      noProvinceCount: 0,

      reviewingId: null,

      /* ── คำนวณโซน/สถานี ── */
      isRecalcModalOpen: false,
      isPreviewingRecalc: false,
      isSavingRecalc: false,
      recalcingId: null,
      recalcTarget: null,
      recalcPreview: null,
      recalcError: '',

      /*
       * โซนที่จะบันทึก — ตั้งต้นเท่ากับ "ผลที่จะได้ถ้าไม่แตะอะไร" แล้วให้คนแก้ได้
       * recalcZoneBaseline เก็บค่าตั้งต้นไว้เทียบ ถ้าไม่ต่างกันจะไม่ส่ง zone_ids ไป
       * ฝั่ง API เลย พฤติกรรมเดิมจึงเหมือนเป๊ะสำหรับคนที่แค่กดยืนยันผ่าน ๆ
       */
      recalcZoneIds: [],
      recalcZoneBaseline: [],

      /* รายชื่อโซนทั้งหมด — โหลดครั้งแรกที่เปิด popup แล้วเก็บไว้ใช้ซ้ำ (~170 KB) */
      zones: [],
      isLoadingZones: false,
      zoneLoadError: '',

      /* ── แก้พิกัดเอง ── */
      isCoordModalOpen: false,
      isSavingCoord: false,
      coordTarget: null,
      coordLat: '',
      coordLng: '',
      coordError: '',
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalRows / this.perPage))
    },
    /*
     * การ์ดสรุป = ปุ่มกรองในตัว แต่ละใบบอกว่ากดแล้วจะเห็นชุดไหน
     * ⚠️ ตัวเลขพวกนี้นับทั้งฐานเสมอ ไม่ขยับตามขอบเขตที่เลือกอยู่ (ฝั่ง API ตั้งใจให้เป็นแบบนั้น)
     * ต่างจากตัวเลขในเมนูกรองด้านล่างที่นับตามขอบเขต
     */
    stats() {
      return [
        {
          key: 'total', label: 'Place ทั้งหมด', value: this.summary.total_places, patch: { zone: 'all', reason: '' },
        },
        {
          key: 'without', label: 'ยังไม่มีโซน', value: this.summary.without_zone, patch: { zone: 'without', reason: '' },
        },
        {
          key: 'with', label: 'มีโซนแล้ว', value: this.summary.with_zone, patch: { zone: 'with', reason: '' },
        },
        {
          key: 'no_link', label: 'ไม่เคยผูกโซน', value: this.summary.no_link, patch: { zone: 'without', reason: 'no_link' },
        },
        {
          key: 'deleted', label: 'ผูกไว้แต่โซนถูกลบ', value: this.summary.zone_deleted, patch: { zone: 'without', reason: 'zone_deleted' },
        },
      ]
    },
    /* ป้ายพร้อมจำนวน แบบเดียวกับเมนูกรองอื่นบนหน้านี้ */
    zoneOptions() {
      const n = v => Number(v || 0).toLocaleString('en-US')

      return Object.entries(ZONE_STATES).map(([value, v]) => ({
        value,
        text: `${v.label} (${n(this.summary[v.count])})`,
      }))
    },
    cardTitle() {
      return (ZONE_STATES[this.zoneFilter] || ZONE_STATES.without).title
    },
    /* ⚠️ zone/v1/lists คืน name เป็น map ของภาษา ไม่ใช่สตริง — ต้องแกะก่อนเอาไปโชว์ */
    zoneChoices() {
      return this.zones.map(z => ({
        id: z.id,
        title: z.name?.[this.$i18n.locale] || z.name?.th || z.name?.en || z.title || `zone #${z.id}`,
      }))
    },
    computedZoneIds() {
      return (this.recalcPreview?.zones || []).map(z => z.id)
    },
    /*
     * แก้จากค่าตั้งต้นหรือยัง — ใช้ตัดสินว่าจะส่ง zone_ids ไปหรือปล่อยให้ API คำนวณเอง
     * เรียงก่อนเทียบ เพราะลำดับที่คนคลิกเลือกไม่ได้มีความหมายอะไร
     */
    zonesChanged() {
      const norm = list => [...list].map(Number).sort((a, b) => a - b).join(',')

      return norm(this.recalcZoneIds) !== norm(this.recalcZoneBaseline)
    },
    /* ยืนยันได้ต่อเมื่อมีข้อเสนอที่ผูกได้จริง — อย่างน้อยต้องรู้ถึงระดับอำเภอ */
    canApplyArea() {
      const s = this.areaSuggestion

      return !!(s && (s.tambon_id || s.amphure_id))
    },
    confidenceLabel() {
      return {
        exact: 'พิกัดตกอยู่ในขอบเขตตำบลนี้จริง (กรมแผนที่ทหาร)',
        high: 'ตรงกับที่อยู่ที่ Google เก็บไว้ — ถึงระดับตำบล',
        medium: 'รู้ถึงระดับอำเภอ — ตำบลยังไม่ชัด จะไม่เขียนตำบล',
        low: 'เดาจาก place ข้างเคียง — ระดับตำบลผิดได้ราว 1 ใน 5',
      }[this.areaSuggestion?.confidence] || ''
    },
    confidenceVariant() {
      return {
        exact: 'light-success',
        high: 'light-info',
        medium: 'light-warning',
        low: 'light-danger',
      }[this.areaSuggestion?.confidence] || 'light-secondary'
    },
    /* ที่มาของคำตอบ เขียนให้คนตรวจตัดสินใจได้ว่าจะเชื่อหรือไม่ */
    sourceNote() {
      const s = this.areaSuggestion

      if (!s) return ''
      if (s.match === 'boundary') {
        return 'หาจากขอบเขตการปกครองจริง — คำตอบนี้แม่นเท่าที่พิกัดของ place ถูกต้อง'
      }
      if (s.match === 'neighbour') {
        return `อ้างอิงจาก "${s.neighbour.name}" ที่อยู่ห่าง ${Number(s.neighbour.distance_m).toLocaleString('en-US')} ม.`
      }

      return s.formatted_address ? `จากที่อยู่: ${s.formatted_address}` : ''
    },
    /* ตารางเทียบของเดิมกับของใหม่ — ช่องที่เปลี่ยนจะถูกไฮไลต์ */
    areaRows() {
      const before = this.areaCurrent || {}
      const s = this.areaSuggestion || {}
      const dash = v => (v === null || v === undefined || v === '' || v === '0' || v === 0 ? '—' : v)

      return [
        { label: 'จังหวัด', beforeRaw: before.province_th, afterRaw: s.province_th },
        { label: 'อำเภอ / เขต', beforeRaw: before.amphure_th, afterRaw: s.amphure_th },
        { label: 'ตำบล / แขวง', beforeRaw: before.tambon_th, afterRaw: s.tambon_th },
        { label: 'รหัสไปรษณีย์', beforeRaw: before.zip_code, afterRaw: s.tambon_id ? s.zip_code : null },
      ].map(r => ({
        label: r.label,
        before: dash(r.beforeRaw),
        after: dash(r.afterRaw),
        changed: dash(r.beforeRaw) !== dash(r.afterRaw) && dash(r.afterRaw) !== '—',
      }))
    },
    /*
     * ตรวจค่าฝั่งหน้าเว็บให้ตรงกับที่ setCoord ตรวจฝั่ง API เป๊ะ ๆ เพื่อให้คนเห็นปัญหา
     * ตั้งแต่ตอนพิมพ์ ไม่ใช่หลังกดบันทึกแล้วโดนตีกลับ — ตัวจริงที่กันคือฝั่ง API เสมอ
     */
    coordProblem() {
      if (this.coordLat === '' || this.coordLng === '') return ''

      const lat = Number(this.coordLat)
      const lng = Number(this.coordLng)

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return 'lat / lng ต้องเป็นตัวเลข'
      if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return 'lat ต้องอยู่ใน -90..90 และ lng -180..180'
      if (lat === 0 || lng === 0) return 'lat / lng เป็น 0 ไม่ได้ (ถือว่าไม่มีพิกัด)'

      return ''
    },
    canSaveCoord() {
      return this.coordLat !== '' && this.coordLng !== '' && !this.coordProblem
    },
    /* ป้ายกำกับพร้อมจำนวน ใช้เป็นเมนูกรองได้เลยโดยไม่ต้องเพิ่มการ์ดอีกแถว */
    statusOptions() {
      const s = this.summary

      return [
        { value: '', text: 'ทั้งหมด' },
        ...Object.entries(PROGRESS).map(([key, v]) => ({
          value: key,
          text: `${v.label} (${Number(s[key] || 0).toLocaleString('en-US')})`,
        })),
      ]
    },
    /*
     * ถามแค่ "ผูกที่อยู่แล้ว/ยัง" — ตัวกรอง status ตอบคำถามนี้ไม่ตรง เพราะมันเป็นลำดับขั้น
     * ตัวที่ติดขั้นต้นกว่าชนะ (place ที่มีที่อยู่ครบแต่ไม่มีพิกัดจะกลายเป็น no_coord)
     */
    hasAddressOptions() {
      const s = this.summary
      const n = v => Number(v || 0).toLocaleString('en-US')

      return [
        { value: '', text: 'ทั้งหมด' },
        { value: '1', text: `ผูกที่อยู่แล้ว (${n(s.address_linked)})` },
        { value: '0', text: `ยังไม่ผูกที่อยู่ (${n(s.address_missing)})` },
      ]
    },
    coordStatusOptions() {
      const s = this.summary

      return [
        { value: '', text: 'ทั้งหมด' },
        ...Object.entries(COORD_STATUS).map(([key, v]) => ({
          value: key,
          text: `${v.label} (${Number(s[`coord_${key}`] || 0).toLocaleString('en-US')})`,
        })),
      ]
    },
    provinceOptions() {
      return [
        { value: '', text: 'ทุกจังหวัด' },
        { value: 'none', text: `ไม่ระบุจังหวัด (${Number(this.noProvinceCount || 0).toLocaleString('en-US')})` },
        ...this.provinces.map(p => ({ value: p.id, text: `${p.name_th} (${p.places})` })),
      ]
    },
    /* พารามิเตอร์ชุดเดียวกันทั้งตอนแสดงและตอน export ผลจะได้ตรงกันเสมอ */
    filterParams() {
      return {
        zone_state: this.zoneFilter,
        ...(this.search ? { search: this.search } : {}),
        ...(this.reasonFilter ? { reason: this.reasonFilter } : {}),
        ...(this.activeFilter !== '' ? { is_active: this.activeFilter } : {}),
        ...(this.statusFilter ? { status: this.statusFilter } : {}),
        ...(this.coordStatusFilter ? { coord_status: this.coordStatusFilter } : {}),
        ...(this.hasAddressFilter !== '' ? { has_address: this.hasAddressFilter } : {}),
        ...(this.provinceFilter !== '' ? { province_id: this.provinceFilter } : {}),
      }
    },
  },
  mounted() {
    this.fetchPlaces()
    this.fetchProvinces()
  },
  methods: {
    reasonLabel(reason) {
      return (REASON[reason] || {}).label || reason || '—'
    },
    reasonVariant(reason) {
      return (REASON[reason] || {}).variant || 'light-secondary'
    },

    statusLabel(v) {
      return (PROGRESS[v] || {}).label || v || '—'
    },
    statusVariant(v) {
      return (PROGRESS[v] || {}).variant || 'light-secondary'
    },

    /*
     * ── ตัดสินใจกับพิกัดที่แนะนำ ──
     * ใช้ endpoint เดียวกับหน้า coord-review ทั้งคู่ ไม่ได้เขียนตัวใหม่ สถานะที่ได้จึงตรงกัน
     * ทั้งสองหน้า และ apply ยังเก็บ prev_lat/prev_lng ให้ย้อนกลับได้เหมือนเดิม
     */
    async applySuggested(item) {
      await this.runCoordAction(item, 'apply', {}, `ใช้พิกัดที่แนะนำกับ "${item.place_name}" แล้ว`)
    },

    async markReviewed(item, status) {
      await this.runCoordAction(item, 'save', {
        status,
        note: 'ตรวจจากหน้า place/without-zone',
      }, `บันทึกว่า "${item.place_name}" พิกัดเดิมถูกแล้ว`)
    },

    async runCoordAction(item, action, extra, successText) {
      this.reviewingId = item.place_id

      try {
        const response = await axios.post(`place/v1/coord-review/${action}`, {
          place_id: item.place_id,
          ...extra,
        })

        if (!response?.data?.success) throw new Error(response?.data?.message || 'บันทึกไม่สำเร็จ')

        this.$bvToast.toast(successText, { title: 'บันทึกแล้ว', variant: 'success', solid: true })

        /* สถานะทั้งสองชุดคิดสดฝั่ง API — โหลดใหม่แล้ว badge กับตัวเลขสรุปจะขยับเอง */
        await this.fetchPlaces()
      } catch (error) {
        this.$bvToast.toast(
          `ไม่สำเร็จ: ${error?.response?.data?.message || error.message}`,
          { title: 'ผิดพลาด', variant: 'danger', solid: true },
        )
      } finally {
        this.reviewingId = null
      }
    },

    coordStatusLabel(v) {
      return (COORD_STATUS[v] || {}).label || v || 'ยังไม่ตรวจ'
    },
    coordStatusVariant(v) {
      return (COORD_STATUS[v] || {}).variant || 'light-secondary'
    },
    shortDate(v) {
      if (!v) return ''
      const d = new Date(v)

      return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('th-TH', { day: '2-digit', month: 'short', year: '2-digit' })
    },

    async fetchProvinces() {
      try {
        const response = await axios.get('place/v1/provinces')

        this.provinces = response?.data?.data || []
        this.noProvinceCount = Number(response?.data?.no_province || 0)
      } catch (error) {
        /* ไม่ได้รายชื่อจังหวัดก็ยังใช้หน้าได้ แค่กรองตามจังหวัดไม่ได้ */
        console.error('Error loading provinces:', error)
      }
    },

    /*
     * ── คำนวณโซน/สถานี ──
     * ขอผลแบบ dry run มาก่อนเสมอ แล้วค่อยยืนยัน เพราะการคำนวณจริงลบ place_zone /
     * place_transits ของเดิมทิ้งทั้งชุดก่อนเขียนใหม่ — ใช้ endpoint เดียวกับหน้า coord-review
     */
    /* โหลดรายชื่อโซนครั้งเดียวตอนเปิด popup ครั้งแรก — ไม่ดึงตอนเปิดหน้าเพราะส่วนใหญ่ไม่ได้ใช้ */
    async loadZones() {
      if (this.zones.length || this.isLoadingZones) return

      this.isLoadingZones = true
      this.zoneLoadError = ''

      try {
        const response = await axios.post('zone/v1/lists', { perpage: 2000, offset: 0 })

        this.zones = response?.data?.data || []
        if (!this.zones.length) this.zoneLoadError = 'ไม่พบรายชื่อโซน'
      } catch (error) {
        this.zoneLoadError = `โหลดรายชื่อโซนไม่สำเร็จ${error?.response?.status ? ` (HTTP ${error.response.status})` : ''}`
      } finally {
        this.isLoadingZones = false
      }
    },

    /*
     * ค่าตั้งต้นของช่องเลือกโซน = ผลที่จะได้ถ้ากดยืนยันโดยไม่แตะอะไร
     *
     * ⚠️ ไม่ใช่ "โซนที่คำนวณได้" เสมอไป — ตอนที่ polygon ไม่เจอโซนเลย ฝั่ง API จะไม่ลบ
     * ของเดิมทิ้ง (ดู recalcZone ใน helpers/place-geo-recalc.helper.js) ผลที่จะได้จริง
     * จึงเป็นโซนเดิม ถ้าตั้งต้นเป็นชุดว่างตามผลคำนวณ การกดยืนยันเฉย ๆ จะกลายเป็น
     * การล้างโซนเดิมทิ้งโดยที่ไม่มีใครตั้งใจ
     */
    resetZones() {
      const preview = this.recalcPreview || {}
      const fallback = preview.zone_current || []

      this.recalcZoneBaseline = (preview.zones?.length ? preview.zones : fallback).map(z => z.id)
      this.recalcZoneIds = [...this.recalcZoneBaseline]
    },

    async openRecalcModal(item) {
      this.recalcTarget = item
      this.recalcPreview = null
      this.recalcError = ''
      this.recalcZoneIds = []
      this.recalcZoneBaseline = []
      this.isRecalcModalOpen = true
      this.isPreviewingRecalc = true
      this.recalcingId = item.place_id

      this.loadZones()

      try {
        const response = await axios.post('place/v1/coord-review/recalc', {
          place_id: item.place_id,
          dry_run: true,
        })

        if (!response?.data?.success) throw new Error(response?.data?.message || 'ดูผลไม่สำเร็จ')

        this.recalcPreview = response.data.data
        this.resetZones()
      } catch (error) {
        this.recalcError = `ดูผลไม่สำเร็จ: ${error?.response?.data?.message || error.message}`
      } finally {
        this.isPreviewingRecalc = false
        this.recalcingId = null
      }
    },

    async confirmRecalc() {
      if (!this.recalcPreview) return

      this.isSavingRecalc = true
      this.recalcError = ''

      try {
        /*
         * ส่ง zone_ids เฉพาะตอนที่คนแก้จากค่าตั้งต้นจริง ๆ
         * ไม่ส่ง = ฝั่ง API คำนวณเองเหมือนเดิมทุกอย่าง รวมถึงกฎ "ไม่เจอโซน = ไม่ลบของเดิม"
         */
        const response = await axios.post('place/v1/coord-review/recalc', {
          place_id: this.recalcTarget.place_id,
          ...(this.zonesChanged ? { zone_ids: this.recalcZoneIds } : {}),
        })

        if (!response?.data?.success) throw new Error(response?.data?.message || 'คำนวณไม่สำเร็จ')

        this.isRecalcModalOpen = false
        this.$bvToast.toast(`คำนวณโซน/สถานีของ "${this.recalcTarget.place_name}" แล้ว`, {
          title: 'บันทึกแล้ว',
          variant: 'success',
          solid: true,
        })

        /* สถานะคิดสดฝั่ง API — โหลดใหม่แล้วแถวนี้จะขยับจาก need_recalc เอง */
        await this.fetchPlaces()
      } catch (error) {
        this.recalcError = `คำนวณไม่สำเร็จ: ${error?.response?.data?.message || error.message}`
      } finally {
        this.isSavingRecalc = false
      }
    },

    /* ── ตัวช่วยแสดงพิกัด — ชุดเดียวกับหน้า coord-review ── */
    fmt(v) {
      return v === null || v === undefined ? '—' : Number(v).toFixed(5)
    },
    mapUrl(lat, lng) {
      return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    },
    severityVariant(s) {
      return SEVERITY_VARIANT[s] || 'light-secondary'
    },
    distanceText(m) {
      if (m === null || m === undefined) return '—'
      const n = Number(m)

      return n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)} กม.` : `${Math.round(n)} ม.`
    },

    /* ── แก้พิกัดเอง ── */
    openCoordModal(item) {
      this.coordTarget = item
      this.coordError = ''

      /* เริ่มจากค่าปัจจุบัน ถ้ายังไม่มีพิกัดก็ปล่อยว่างให้กรอกเอง */
      this.coordLat = Number(item.has_latlng) ? String(item.lat) : ''
      this.coordLng = Number(item.has_latlng) ? String(item.lng) : ''
      this.isCoordModalOpen = true
    },

    useSuggestedCoord() {
      this.coordLat = String(this.coordTarget.suggested_lat)
      this.coordLng = String(this.coordTarget.suggested_lng)
    },

    /*
     * ใช้ endpoint เดียวกับหน้า coord-review — ไม่ได้เขียนตัวใหม่ จึงได้ validation,
     * การเก็บ prev_lat/prev_lng และ audit trail เหมือนกันทั้งสองหน้า
     *
     * ⚠️ ไม่สั่ง recalc ต่อให้อัตโนมัติ โซน/สถานีที่ผูกไว้จะยังเป็นของพิกัดเก่าจนกว่าจะสั่งเอง
     * ที่หน้า coord-review — บอกไว้ใน popup แล้ว
     */
    async saveCoord() {
      if (!this.canSaveCoord) return

      this.isSavingCoord = true
      this.coordError = ''

      try {
        const response = await axios.post('place/v1/coord-review/set-coord', {
          place_id: this.coordTarget.place_id,
          lat: Number(this.coordLat),
          lng: Number(this.coordLng),
          note: 'แก้พิกัดเองในหน้า place/without-zone',
        })

        if (!response?.data?.success) throw new Error(response?.data?.message || 'บันทึกไม่สำเร็จ')

        this.isCoordModalOpen = false
        this.$bvToast.toast(`อัปเดตพิกัดของ "${this.coordTarget.place_name}" แล้ว`, {
          title: 'บันทึกแล้ว',
          variant: 'success',
          solid: true,
        })

        await this.fetchPlaces()
      } catch (error) {
        this.coordError = `บันทึกไม่สำเร็จ: ${error?.response?.data?.message || error.message}`
      } finally {
        this.isSavingCoord = false
      }
    },

    /*
     * ── ผูกที่อยู่: preview ก่อน แล้วค่อย apply ──
     * preview ไม่แตะข้อมูลเลย ตัวที่เขียนจริงคือ applyArea ซึ่งเรียกได้จากปุ่มยืนยันเท่านั้น
     */
    async openAreaModal(item) {
      this.areaTarget = item
      this.areaCurrent = null
      this.areaSuggestion = null
      this.areaError = ''
      this.isAreaModalOpen = true
      this.isPreviewingArea = true
      this.previewingId = item.place_id

      try {
        const response = await axios.post('place/v1/admin-area/preview', {
          place_id: item.place_id,
        })

        if (!response?.data?.success) throw new Error(response?.data?.message || 'preview ไม่สำเร็จ')

        this.areaCurrent = response.data.data?.place || null
        this.areaSuggestion = response.data.data?.suggestion || null
      } catch (error) {
        this.areaError = `หาข้อมูลที่อยู่ไม่สำเร็จ: ${error?.response?.data?.message || error.message}`
      } finally {
        this.isPreviewingArea = false
        this.previewingId = null
      }
    },

    /*
     * ⚠️ ส่งไปแค่ tambon_id (หรือ amphure_id ตอนที่ยังไม่รู้ตำบล)
     * จังหวัด/อำเภอ/รหัสไปรษณีย์ ฝั่ง API ไล่หาจากตารางทะเบียนเอง เพื่อไม่ให้ place
     * ถือคู่ที่ไม่ได้อยู่ด้วยกันจริง — คอลัมน์พวกนี้ไม่มี foreign key คุมไว้
     */
    async applyArea() {
      if (!this.canApplyArea) return

      this.isApplyingArea = true
      this.areaError = ''

      try {
        const s = this.areaSuggestion
        const response = await axios.post('place/v1/admin-area/apply', {
          place_id: this.areaTarget.place_id,
          ...(s.tambon_id ? { tambon_id: s.tambon_id } : { amphure_id: s.amphure_id }),
        })

        if (!response?.data?.success) throw new Error(response?.data?.message || 'บันทึกไม่สำเร็จ')

        this.isAreaModalOpen = false
        this.$bvToast.toast(`ผูกที่อยู่ให้ "${this.areaTarget.place_name}" แล้ว`, {
          title: 'บันทึกแล้ว',
          variant: 'success',
          solid: true,
        })

        /*
         * รายการนี้ยังค้างอยู่ในตารางต่อไป เพราะหน้านี้นับ "ไม่มีโซน" ซึ่งเป็นคนละเรื่องกับ
         * ที่อยู่ — โหลดใหม่เพื่อให้ยอดสรุปตรงกับ DB ไม่ใช่เพื่อให้แถวหายไป
         */
        await this.fetchPlaces()
      } catch (error) {
        this.areaError = `บันทึกไม่สำเร็จ: ${error?.response?.data?.message || error.message}`
      } finally {
        this.isApplyingArea = false
      }
    },

    async fetchPlaces() {
      try {
        this.isLoading = true
        this.loadError = ''

        const response = await axios.post('place/v1/without-zone', {
          perpage: this.perPage,
          offset: (this.currentPage - 1) * this.perPage,
          ...this.filterParams,
        })

        this.places = response?.data?.data || []
        this.totalRows = Number(response?.data?.total || 0)
        this.summary = response?.data?.summary || {}
      } catch (error) {
        this.places = []
        this.totalRows = 0
        this.loadError = `โหลดข้อมูลไม่สำเร็จ${error?.response?.status ? ` (HTTP ${error.response.status})` : ''}`
        console.error('Error loading places without zone:', error)
      } finally {
        this.isLoading = false
      }
    },

    onFilterChange() {
      this.currentPage = 1
      this.fetchPlaces()
    },

    /*
     * b-pagination ส่งเลขหน้าใหม่มาทาง argument แต่ยังไม่ได้อัปเดต v-model ตอนที่ยิง event
     * ถ้าอ่านจาก this.currentPage จะได้หน้าเก่าไปหนึ่งก้าวเสมอ
     */
    onPageChange(page) {
      this.currentPage = page
      this.$nextTick(this.fetchPlaces)
    },

    isStatActive(stat) {
      return this.zoneFilter === stat.patch.zone && this.reasonFilter === stat.patch.reason
    },

    applyStat(stat) {
      this.zoneFilter = stat.patch.zone
      this.reasonFilter = stat.patch.reason
      this.onFilterChange()
    },

    /*
     * ย้ายไปดูฝั่งที่ผูกโซนแล้ว = สาเหตุที่เลือกค้างไว้ใช้ไม่ได้อีก (ทุกแถวเป็น has_zone)
     * ถ้าไม่ล้าง ตารางจะว่างเปล่าโดยที่ผู้ใช้ไม่รู้ว่าเพราะตัวกรองที่มองไม่เห็นแล้ว
     */
    onZoneChange() {
      if (this.zoneFilter === 'with') this.reasonFilter = ''
      this.onFilterChange()
    },

    /* ดาวน์โหลดตามตัวกรองที่เลือกอยู่ ไม่ใช่แค่หน้าที่เห็น */
    async exportCsv() {
      try {
        this.isExporting = true
        this.loadError = ''

        const rows = []
        for (let offset = 0; ; offset += EXPORT_CHUNK) {
          // eslint-disable-next-line no-await-in-loop
          const response = await axios.post('place/v1/without-zone', {
            perpage: EXPORT_CHUNK,
            offset,
            ...this.filterParams,
          })
          const chunk = response?.data?.data || []

          rows.push(...chunk)
          if (chunk.length < EXPORT_CHUNK) break
        }

        this.downloadCsv(rows)
      } catch (error) {
        this.loadError = 'ดาวน์โหลด CSV ไม่สำเร็จ'
        console.error('Error exporting places without zone:', error)
      } finally {
        this.isExporting = false
      }
    },

    downloadCsv(rows) {
      const header = ['place_id', 'place_name', 'code', 'reason', 'zone_names', 'zone_col_name', 'has_latlng', 'is_active', 'lat', 'lng']

      /* RFC4180: ครอบด้วย " เมื่อมี , " หรือขึ้นบรรทัดใหม่ แล้ว escape " เป็น "" */
      const cell = v => {
        if (v === null || v === undefined) return ''
        const s = String(v)

        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
      }

      const csv = [
        header.join(','),
        ...rows.map(r => header.map(h => cell(r[h])).join(',')),
      ].join('\n')

      // \uFEFF = BOM ให้ Excel อ่านภาษาไทยไม่เป็นตัวยึกยือ
      // เขียนเป็น escape ไม่ใช่อักขระจริง ไม่งั้นมันมองไม่เห็นในซอร์สและ eslint ก็ฟ้อง
      const blob = new Blob([`\uFEFF${csv}\n`], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')

      a.href = url
      a.download = `places-zone-${this.zoneFilter}-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
  },
}
</script>

<style scoped>
.stat-card {
  cursor: pointer;
  transition: border-color 0.15s ease;
  border: 1px solid transparent;
}

.stat-card-active {
  border-color: #7367f0;
}
</style>
